import {
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';
import {UserContext} from '../../contexts/userContext';
import {ApiClient} from '../../Utils/ApiClient';
import {RegularSpacer} from '../BasicComponents/Spacers';
import ClientPicker from '../UserInput/ClientPicker';

const ImageUploadPage = () => {
  const {t} = useTranslation();
  const [displayText, setDisplayText] = useState('');
  const [batch, setBatch] = useState('');
  const [file, setFile] = useState(null);
  const {user, hasClientPicker, currentClient, getUserConfig} = useContext(UserContext);
  const config = getUserConfig(true);
  const [selectedClient, setSelectedClient] = useState(currentClient);
  const [crop, setCrop] = useState('Strelitzia');
  const [clientErrorMessage, setClientErrorMessage] = useState('');
  const [cropErrorMessage, setCropErrorMessage] = useState('');
  const [batchErrorMessage, setBatchErrorMessage] = useState('');
  const [imageErrorMessage, setImageErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState('');
  const hasCropList = (config?.filter_on_crops && config.filter_on_crops.length > 0);
  const hasRecipeList = (config?.recipes && config.recipes.length > 0);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file === undefined) {
      return;
    }
    setFile(file);
    setDisplayText('Selected ' + file.name);
    setImageErrorMessage('');
  };


  const handleSubmit = async () => {
    setIsSubmitted(true);
    setSuccessMessage('');
    if (!file) {
      setImageErrorMessage('Please select an image file');
    } else {
      setImageErrorMessage('');
    }
    if (!selectedClient) {
      setClientErrorMessage('Please select a client');
    } else {
      setClientErrorMessage('');
    }
    if (!batch) {
      setBatchErrorMessage('Please enter a batch ID');
    } else {
      setBatchErrorMessage('');
    }
    if (!crop) {
      setCropErrorMessage('Please enter a crop');
    } else {
      setCropErrorMessage('');
    }

    if (!file || !selectedClient || !batch || !crop) {
      setIsSubmitted(false);
      return;
    }
    setErrorMessage('');

    try {
      const snapshotId = uuidv4();
      await uploadImage(snapshotId, file);
      await createSnapshot(snapshotId, batch);
      setFile(null);
      setDisplayText('');
      setSuccessMessage('Snapshot uploaded successfully!');
      setIsSubmitted(false);
    } catch (error) {
      setErrorMessage('Error uploading snapshot: ' + error.message);
    }
    setIsSubmitted(false);
  };

  const uploadImage = async (snapshotId, file) => {
    console.log(file);
    const imageUrl = snapshotId + '/HighRes.jpg';
    const imageResult = await ApiClient.uploadImage(selectedClient, imageUrl, file);
    console.log(imageResult);
  };

  const createSnapshot = async (snapshotId, batch) => {
    const snapshot = {
      '_id': snapshotId,
      'client': selectedClient,
      'device': 'WebApp',
      'collection_date': new Date().toISOString(),
      'status': 'created',
      'system_version': '2024-11-18T00:00:00.000Z',
      'author': selectedClient,
      'data': {
        'images': {
          'rawHD': snapshotId + '/HighRes.jpg',
        },
        'batch_id': batch,
        'crop': crop,
      },
    };
    if (hasRecipeList && selectedRecipe) {
      snapshot.data['analysis_pipeline_name'] = selectedRecipe;
    }
    console.log(snapshot);
    return ApiClient.createSnapshot(snapshot);
  };

  const handleTextChange = (setter) => (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9\-_]/g, '_');
    setter(value);
  };

  return (
    <div style={{'width': '100%', 'maxWidth': '750px'}}>
      <Card>
        <CardContent>
          <h2>{t('Upload Snapshot')}</h2>
          {hasClientPicker?
          <ClientPicker clients={user.clients}
            label='Client*'
            useUserContext={true}
            defaultClient={selectedClient}
            onClientChanged={setSelectedClient}
            triggerOnEmpty={true}
            hideCard={true}/>: ''}
          {clientErrorMessage && (
            <Typography style={{color: 'red', marginTop: '1rem'}}>
              {clientErrorMessage}
            </Typography>
          )}
          <RegularSpacer/>
          {hasCropList ? (
            <FormControl
              variant="outlined"
              margin="normal"
              required
              fullWidth
              error={isSubmitted && !crop}
            >
              <InputLabel id="crop-label">{t('Crop')}</InputLabel>
              <Select
                labelId="crop-label"
                value={crop}
                onChange={(e) => setCrop(e.target.value)}
                label={t('Crop')}
                id="crop"
              >
                {config.filter_on_crops.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              value={crop}
              onChange={handleTextChange(setCrop)}
              name="crop"
              label={t('Crop')}
              type="text"
              id="crop"
              error={isSubmitted && !crop}
              helperText={isSubmitted && !crop ? 'Crop is required' : ''}
            />
          )}
          {cropErrorMessage && (
            <Typography style={{color: 'red', marginTop: '1rem'}}>
              {cropErrorMessage}
            </Typography>
          )}
          {hasRecipeList && (
            <FormControl
              variant="outlined"
              margin="normal"
              required
              fullWidth
              error={isSubmitted && !selectedRecipe}
            >
              <InputLabel id="recipe-label">{t('Recipe')}</InputLabel>
              <Select
                labelId="recipe-label"
                value={selectedRecipe}
                onChange={(e) => setSelectedRecipe(e.target.value)}
                label={t('Recipe')}
                id="recipe"
              >
                {config.recipes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            value={batch}
            onChange={handleTextChange(setBatch)}
            name="batch id"
            label={t('Batch ID')}
            type="text"
            id="batch"
            error={isSubmitted && !batch}
            helperText={isSubmitted && !batch ? 'Batch ID is required' : ''}
          />
          {batchErrorMessage && (
            <Typography style={{color: 'red', marginTop: '1rem'}}>
              {batchErrorMessage}
            </Typography>
          )}
          <RegularSpacer/>
          <input
            accept="image/*"
            type="file"
            capture="environment"
            onChange={handleFileUpload}
            style={{display: 'none'}}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button variant="outlined" color="primary" component="span">
            Select image file
            </Button>
            {' ' + displayText}
          </label>
          {imageErrorMessage && (
            <Typography style={{color: 'red', marginTop: '1rem'}}>
              {imageErrorMessage}
            </Typography>
          )}
          <RegularSpacer size={5}/>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSubmitted}>
            Submit
          </Button>
          {errorMessage && (
            <Typography style={{color: 'red', marginTop: '1rem'}}>
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography style={{color: 'green', marginTop: '1rem'}}>
              {successMessage}
            </Typography>
          )}

          <RegularSpacer/>
        </CardContent>
        <div style={{textAlign: 'right', padding: '8px 16px', fontSize: '0.75rem', color: '#666'}}>
          * Only letters, numbers, -, and _ allowed; others will be replaced
        </div>
      </Card>
    </div>
  );
};

export default ImageUploadPage;
